import { A } from '@mobily/ts-belt';
import { Box, CardContent, CardHeader, CircularProgress, Grid } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import { makeStyles } from 'tss-react/mui';

import { Challenge } from '@/components/forms/challenge';
import { DynamicTranslation } from '@/dynamic-translation';

const useStyles = makeStyles()(() => ({
  progress: {
    display: 'block',
    margin: '0 auto',
  },
}));

const totalNumDots = 3;

export interface Properties {
  challenge: Challenge | null;
}

const LoadingSplashScreen: FC<Properties> = ({ challenge }) => {
  const { classes } = useStyles();
  const { t } = useTranslation('auth');
  const [dots, setDots] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDots((dots + 1) % (totalNumDots + 1));
    }, 600);

    return () => {
      clearTimeout(intervalId);
    };
  }, [dots]);

  const preservedPosition = A.repeat(totalNumDots - dots, '.').map((dot, i) => (
    <Box component="span" key={i} sx={{ opacity: 0 }}>
      {dot}
    </Box>
  ));

  return (
    <>
      <CardHeader
        titleTypographyProps={{ align: 'center' }}
        subheaderTypographyProps={{ align: 'center' }}
        subheader={t('auth:hangOnForAMoment', 'Hang on for a moment.')}
        {...match(challenge)
          .with(Challenge.NEW_PASSWORD_REQUIRED, () => ({
            title: (
              <Trans i18nKey="auth:renewingPassword">
                Renewing your password{{ dots: '.'.repeat(dots) }}
                <DynamicTranslation i18nIsDynamicList>{preservedPosition}</DynamicTranslation>
              </Trans>
            ),
          }))
          .with(Challenge.RESET_PASSWORD, () => ({
            title: (
              <Trans i18nKey="auth:resettingPassword">
                Resetting your password{{ dots: '.'.repeat(dots) }}
                <DynamicTranslation i18nIsDynamicList>{preservedPosition}</DynamicTranslation>
              </Trans>
            ),
          }))
          .with(Challenge.SMS_MFA, Challenge.SOFTWARE_TOKEN_MFA, () => ({
            title: (
              <Trans i18nKey="auth:submittingMFA">
                Submitting your multi-factor verification code{{ dots: '.'.repeat(dots) }}
                <DynamicTranslation i18nIsDynamicList>{preservedPosition}</DynamicTranslation>
              </Trans>
            ),
          }))
          .otherwise(() => ({
            title: (
              <Trans i18nKey="auth:signingIn">
                Signing you in{{ dots: '.'.repeat(dots) }}
                <DynamicTranslation i18nIsDynamicList>{preservedPosition}</DynamicTranslation>
              </Trans>
            ),
          }))}
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CircularProgress className={classes.progress} size={74} color="primary" />
          </Grid>
        </Grid>
      </CardContent>
    </>
  );
};

export default LoadingSplashScreen;
